
import React, { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { ChartDataItem } from "@/hooks/useReportsData";
import { printReportChart } from "@/utils/printing/reportsPrinter";
import PageSelectionDialog from "@/components/shared/PageSelectionDialog";
import FormatSelectionDialog from "@/components/shared/FormatSelectionDialog";
import { printReportDataPaginatedAsJPG, printReportDataPaginatedAsPDF } from "@/utils/printing/reportsPaginatedPrinter";
import { toast } from "sonner";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
const RECORDS_PER_PAGE = 12;

interface ProgramDistributionProps {
  data: ChartDataItem[];
}

const ProgramDistribution = ({ data }: ProgramDistributionProps) => {
  const [isPageDialogOpen, setIsPageDialogOpen] = useState(false);
  const [isFormatDialogOpen, setIsFormatDialogOpen] = useState(false);
  const [selectedPages, setSelectedPages] = useState<{ start: number; end: number } | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = () => {
    if (!data || data.length === 0) {
      toast.error("No program distribution data available to download");
      return;
    }

    // Always show page selection dialog first, regardless of page count
    setIsPageDialogOpen(true);
  };

  const handlePageSelection = (startPage: number, endPage: number) => {
    setSelectedPages({ start: startPage, end: endPage });
    setIsPageDialogOpen(false);
    setIsFormatDialogOpen(true);
  };

  const handleFormatDownload = async (downloadFormat: 'jpg' | 'pdf') => {
    if (isPrinting) return;
    
    setIsPrinting(true);
    
    try {
      const startPage = selectedPages?.start || 1;
      const endPage = selectedPages?.end || Math.ceil(data.length / RECORDS_PER_PAGE);
      
      if (downloadFormat === 'pdf') {
        await printReportDataPaginatedAsPDF(data, "Program Distribution", startPage, endPage);
      } else {
        await printReportDataPaginatedAsJPG(data, "Program Distribution", startPage, endPage);
      }
      
    } catch (error) {
      console.error('Error downloading program distribution:', error);
      toast.error("Failed to download program distribution");
    } finally {
      setIsPrinting(false);
      setIsFormatDialogOpen(false);
      setSelectedPages(null);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium">Program Distribution</CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handlePrint}
            disabled={!data || data.length === 0 || isPrinting}
          >
            <Printer className="h-4 w-4 mr-2" />
            {isPrinting ? 'Processing...' : 'Print'}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.length}</div>
          <p className="text-xs text-muted-foreground">Top 5 programs by enrollment</p>
          {data.length > 0 ? (
            <div className="h-[200px] mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {data.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">
              No program data available
            </div>
          )}
        </CardContent>
      </Card>

      <PageSelectionDialog
        isOpen={isPageDialogOpen}
        onClose={() => setIsPageDialogOpen(false)}
        totalRecords={data.length}
        recordsPerPage={RECORDS_PER_PAGE}
        onDownload={handlePageSelection}
        reportTitle="Program Distribution"
      />

      <FormatSelectionDialog
        isOpen={isFormatDialogOpen}
        onClose={() => {
          setIsFormatDialogOpen(false);
          setSelectedPages(null);
        }}
        onDownload={handleFormatDownload}
        totalRecords={data.length}
        reportTitle="Program Distribution"
      />
    </>
  );
};

export default ProgramDistribution;
